import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ExternalLink, ShieldAlert } from "lucide-react";
import { COMPANY } from "@/lib/company";

const mapEmbedSrc = `https://www.google.com/maps?q=${encodeURIComponent(COMPANY.mapAddress)}&output=embed`;
const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(COMPANY.mapAddress)}`;

const CONTACTS = [
  {
    icon: Phone,
    color: "#5B2C91",
    label: "Telefono",
    value: COMPANY.phoneDisplay,
    href: `tel:${COMPANY.phone}`,
    note: "Per informazioni sul servizio",
    testid: "contact-phone-link",
  },
  {
    icon: Mail,
    color: "#0EA5E9",
    label: "Email",
    value: COMPANY.email,
    href: `mailto:${COMPANY.email}`,
    note: "Ti rispondiamo il prima possibile",
    testid: "contact-email-link",
  },
];

export default function ContactPage() {
  return (
    <section className="bg-white py-20 sm:py-28" data-testid="contact-page">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-[#F6F4FA] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#5B2C91]"
          >
            <span className="h-2 w-2 rounded-full bg-[#D4F84A]" />
            Contatti
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1F152E]"
            data-testid="contact-title"
          >
            Parla con noi
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-5 text-base sm:text-lg leading-relaxed text-[#4A4059]"
          >
            Hai bisogno di informazioni sul servizio di visite mediche assistite a domicilio?
            Chiamaci, scrivici o vieni a trovarci.
          </motion.p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10 items-start">
          {/* Left — contact details */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex flex-col gap-5"
          >
            {CONTACTS.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.label}
                  href={c.href}
                  className="group flex items-start gap-4 rounded-3xl bg-white p-6 ring-1 ring-[#5B2C91]/8 shadow-[0_8px_30px_rgb(91,44,145,0.05)] transition-transform duration-300 hover:-translate-y-1"
                  data-testid={c.testid}
                >
                  <span
                    className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl"
                    style={{ backgroundColor: `${c.color}18`, color: c.color }}
                  >
                    <Icon className="h-6 w-6" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8A809A]">{c.label}</p>
                    <p className="font-heading mt-1 text-xl font-semibold text-[#1F152E] group-hover:text-[#5B2C91] transition-colors break-words">
                      {c.value}
                    </p>
                    <p className="mt-1 text-sm text-[#6B6280]">{c.note}</p>
                  </div>
                </a>
              );
            })}

            {/* Address */}
            <div
              className="flex items-start gap-4 rounded-3xl bg-white p-6 ring-1 ring-[#5B2C91]/8 shadow-[0_8px_30px_rgb(91,44,145,0.05)]"
              data-testid="contact-address"
            >
              <span
                className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl"
                style={{ backgroundColor: "#10B98118", color: "#10B981" }}
              >
                <MapPin className="h-6 w-6" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8A809A]">Indirizzo</p>
                <p className="font-heading mt-1 text-xl font-semibold text-[#1F152E]">{COMPANY.name}</p>
                <p className="mt-1 text-sm text-[#4A4059]">{COMPANY.address}</p>
                <a
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5B2C91] hover:text-[#4A2277] transition-colors"
                  data-testid="contact-maps-link"
                >
                  Apri in Google Maps
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>

            <p className="flex items-start gap-2 text-xs text-[#8A809A]">
              <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
              Servizio dedicato a condizioni non emergenziali. In caso di emergenza contattare il
              112/118.
            </p>
          </motion.div>

          {/* Right — map */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden rounded-[2rem] lg:rounded-[2.5rem] shadow-[0_30px_80px_rgba(91,44,145,0.18)] ring-1 ring-white/60"
          >
            <iframe
              title={`Mappa — ${COMPANY.name}, ${COMPANY.mapAddress}`}
              src={mapEmbedSrc}
              className="h-[360px] w-full sm:h-[460px] lg:h-[560px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-map"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
